/**
 * Google Ads conversion firing.
 *
 * Separate from `trackClient` on purpose: PostHog events and Ads conversions
 * answer different questions and must not share a trigger. PostHog wants the
 * ATTEMPT (`report_requested` fires on submit, so the drop-off between attempt
 * and success is visible). Google Ads wants the SUCCESS — a conversion counted
 * for a rate-limited or rejected submission is a lead we never got, and paid
 * bidding would then optimize toward failure.
 *
 * Both entry ramps (`/free-backlink-checker`, `/who-links-to-my-website`) call
 * this, for the same reason `scan-teaser` and `scan-visuals` are shared: two
 * pages computing "did we get a lead" from separate code is a reporting bug no
 * test would catch.
 */

type ConversionInput = {
	/**
	 * The label half of `AW-XXXX/LABEL`, from `ads-conversions.ts`. Nullable so
	 * a conversion action that has not been created in the Ads account yet is a
	 * no-op rather than a broken `send_to`.
	 */
	label: string | null | undefined
	/**
	 * Stable per-conversion key. Prevents a re-render, a back-navigation or a
	 * second submission of the same form from counting twice within the tab.
	 */
	dedupeKey: string
	value?: number
	currency?: string
}

/**
 * Returns true when a conversion was actually handed to gtag.
 *
 * Every bail-out is silent and safe: this is measurement, and a page must never
 * break because an ad tag is missing or blocked.
 */
export function fireAdsConversion({
	label,
	dedupeKey,
	value,
	currency,
}: ConversionInput): boolean {
	if (typeof window === 'undefined') return false

	const id = window.ENV?.GOOGLE_ADS_ID
	if (!id || !label) return false

	// Only mark the dedupe key once gtag is confirmed present. Marking first
	// would permanently suppress a conversion that never reached Google —
	// exactly what happens when the tag is still loading or an ad blocker ate
	// it.
	const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void })
		.gtag
	if (typeof gtag !== 'function') return false

	const storageKey = `ga_conv_${dedupeKey}`
	try {
		if (sessionStorage.getItem(storageKey)) return false
	} catch {
		// Private mode or blocked storage. Losing dedup is better than losing the
		// conversion, so fall through and fire.
	}

	gtag('event', 'conversion', {
		send_to: `${id}/${label}`,
		...(value == null ? {} : { value }),
		...(currency == null ? {} : { currency }),
	})

	try {
		sessionStorage.setItem(storageKey, '1')
	} catch {
		// See above.
	}

	return true
}
